import { Button, Divider, Icon, IconName } from '@blueprintjs/core';
import classes from './RealEstateCard.module.css';
import { AceSelectField } from '../../forms/generic-fields/AceSelectField';
import { useCollateralFormContext } from '../../forms/collateral/context';
import { useCollateralFormStore } from '../../forms/collateral/store';
import { AceTextField } from '../../forms/generic-fields/AceTextField';
import { AceNumberField } from '../../forms/generic-fields/AceNumberField';
import {
  lock_hooks,
  real_estate_hooks,
  useApplicationStatusUtils,
  useLoanApplication,
} from '@axo/shared/data-access/hooks';
import { useCallback, useState } from 'react';
import { currencyFormatter } from '@axo/shared/util/formatters';
import { RealEstateCardHighlightedField } from './RealEstateCardHighlightedField';
import { ConditionalTooltip } from '../ConditionalTooltip/ConditionalTooltip';
import { useConfirmDialog } from '../../hooks/utils/useConfirmDialog';
import { AceCheckboxField } from '../../forms/generic-fields/AceCheckboxField';
import { AceGenericFieldLayout } from '../../forms/generic-fields/AceGenericFieldLayout';

interface RealEstateCardProps {
  applicationID: string;
  estateID: string;
}

export const RealEstateCard: React.FC<RealEstateCardProps> = ({
  estateID,
  applicationID,
}) => {
  const { formState, control, resetField, watch } =
    useCollateralFormContext(applicationID);

  const {
    isEditing: isEditingIds,
    localOnly: localOnlyIds,
    actions: { setIsEditing },
  } = useCollateralFormStore(applicationID);
  const { data: applicationData } = useLoanApplication(applicationID);
  const hasLock = lock_hooks.useCurrentUserHasLock(
    applicationID,
    applicationData?.MarketCountry,
  ).data;
  const currency = applicationData?.Currency;

  const isEditing = Boolean(isEditingIds.has(estateID));

  const formatCurrency = useCallback(
    (value: unknown) => {
      if (!currency) return String(value);

      const amount = Number(value);
      if (amount === 0 || isNaN(amount)) return '-';

      return currencyFormatter(amount, currency, 0);
    },
    [currency],
  );

  const [
    ownershipType,
    collateralID,
    streetName,
    streetNumber,
    streetLetter,
    postcode,
    municipality,
    cadastreBNumber,
    cadastreFNumber,
    cadastreGNumber,
    cadastreKNumber,
    cadastreSNumber,
    source,
    fetchID,
  ] = watch([
    `estates.${estateID}.OwnershipType`,
    `estates.${estateID}.CollateralID`,
    `estates.${estateID}.StreetName`,
    `estates.${estateID}.StreetNumber`,
    `estates.${estateID}.StreetLetter`,
    `estates.${estateID}.Postcode`,
    `estates.${estateID}.Municipality`,
    `estates.${estateID}.CadastreBNumber`,
    `estates.${estateID}.CadastreFNumber`,
    `estates.${estateID}.CadastreGNumber`,
    `estates.${estateID}.CadastreKNumber`,
    `estates.${estateID}.CadastreSNumber`,
    `estates.${estateID}.Source`,
    `estates.${estateID}.FetchID`,
  ]);
  let ownershipIcon: IconName = 'help';
  switch (ownershipType) {
    case 'owner':
      ownershipIcon = 'home';
      break;
    case 'cooperative':
      ownershipIcon = 'office';
      break;
  }

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { confirm } = useConfirmDialog();
  const { mutateAsync: deleteEstate, isLoading: isDeletingEstate } =
    real_estate_hooks.useDeleteRealEstate();
  const {
    mutateAsync: removeApplicationCollateral,
    isLoading: isRemovingApplicationCollateral,
  } = real_estate_hooks.useRemoveApplicationCollateral();

  const { isFinalApplicationStatus } = useApplicationStatusUtils();

  const concatenedAddress = [
    streetName,
    `${streetNumber ?? ''}${streetLetter ?? ''}${
      streetNumber || streetLetter ? ',' : ''
    }`,
    postcode,
    municipality,
  ]
    .filter(Boolean)
    .join(' ');

  const concatenedCadastre = [
    `${cadastreKNumber ?? ''}-${cadastreGNumber ?? ''}`,
    cadastreBNumber ?? '',
    cadastreFNumber ?? '',
    cadastreSNumber ?? '',
  ].join('/');

  const formattedSource =
    source === 'ace' && fetchID
      ? `${source} (Modified EV Data)`
      : (source ?? 'EV');

  return (
    <div className={classes.rootContainer}>
      <div className={classes.headerContainer}>
        <Icon
          icon={ownershipIcon}
          size={24}
          className={classes.ownershipIcon}
        />
        <div className={classes.titlesContainer}>
          <AceGenericFieldLayout
            valueElement={<strong>{concatenedAddress || 'No address'}</strong>}
            inputElement={null}
          />
          <AceSelectField
            editable={isEditing}
            control={control}
            name={`estates.${estateID}.OwnershipType`}
            options={[
              { value: 'owner', label: 'Owner' },
              { value: 'cooperative', label: 'Cooperative' },
              { value: 'other', label: 'Other' },
            ]}
            formatter={(value) => (value ? String(value) : '-')}
          />
          <AceSelectField
            editable={isEditing}
            control={control}
            name={`estates.${estateID}.RealEstateType`}
            options={[
              { value: 'house', label: 'House' },
              { value: 'holiday_home', label: 'Holiday home' },
              { value: 'duplex_house', label: 'Duplex house' },
              { value: 'vacant_plot', label: 'Vacant plot' },
              { value: 'row_house', label: 'Row house' },
              { value: 'apartment', label: 'Apartment' },
              { value: 'garage', label: 'Garage' },
              { value: 'commercial', label: 'Commercial' },
              { value: 'agricultural', label: 'Agricultural' },
              { value: 'public', label: 'Public' },
              { value: 'other', label: 'Other' },
            ]}
            formatter={(value) => (value ? String(value) : '-')}
          />
        </div>
      </div>
      <Divider className={classes.divider} />
      <div className={classes.highlightedContainer}>
        <RealEstateCardHighlightedField
          label="Market Value Estimate"
          inputElement={
            <AceNumberField
              editable={isEditing}
              control={control}
              name={`estates.${estateID}.TotalMarketValueEstimate`}
              formatter={(value) => (
                <div className={classes.highlightedValueContainer}>
                  <strong>{formatCurrency(value)}</strong>
                </div>
              )}
            />
          }
        />
        <RealEstateCardHighlightedField
          label="Market Value Approved Estimate"
          inputElement={
            <AceNumberField
              editable={isEditing}
              control={control}
              name={`estates.${estateID}.TotalMarketValueEstimateApproved`}
              formatter={(value) => (
                <div className={classes.highlightedValueContainer}>
                  <strong>{formatCurrency(value)}</strong>
                </div>
              )}
            />
          }
        />
        <RealEstateCardHighlightedField
          label="Area score"
          inputElement={
            <AceNumberField
              editable={isEditing}
              control={control}
              name={`estates.${estateID}.MarketScoreArea`}
              formatter={(value) => (
                <div className={classes.highlightedValueContainer}>
                  <strong>{value ? String(value) : '-'}</strong>
                </div>
              )}
            />
          }
        />
        <RealEstateCardHighlightedField
          label="Market Score Municipality"
          inputElement={
            <AceNumberField
              editable={isEditing}
              control={control}
              name={`estates.${estateID}.MarketScoreMunicipality`}
              formatter={(value) => (
                <div className={classes.highlightedValueContainer}>
                  <strong>{value ? String(value) : '-'}</strong>
                </div>
              )}
            />
          }
        />
      </div>
      <Divider className={classes.divider} />
      <div className={classes.checkboxContainer}>
        <AceCheckboxField
          editable={true}
          disabled={!isEditing}
          control={control}
          name={`isCollateral.${estateID}`}
          description="Add as collateral"
        />
      </div>
      <Button
        variant="minimal"
        onClick={() => setIsDetailsOpen((isOpen) => !isOpen)}
        endIcon={isDetailsOpen ? 'chevron-up' : 'chevron-down'}
      >
        View details
      </Button>
      {isDetailsOpen && (
        <div className={classes.extraFieldsContainer}>
          {!isEditing && (
            <AceGenericFieldLayout
              label="Source:"
              valueElement={<strong>{formattedSource}</strong>}
              inputElement={null}
            />
          )}
          <AceSelectField
            editable={isEditing}
            control={control}
            name={`estates.${estateID}.PersonID`}
            label="Owner:"
            options={[
              { value: applicationData?.PersonID, label: 'Main applicant' },
              {
                value: applicationData?.CoApplicantPersonID,
                label: 'Co-applicant',
              },
            ].filter((x) => x.value)}
            formatter={(value) => (value ? String(value) : '-')}
          />
          <AceTextField
            editable={isEditing}
            control={control}
            name={`estates.${estateID}.EstimatedValueReferenceNumber`}
            label="Estimated value reference number:"
            formatter={(value) => (
              <strong>{value ? String(value) : '-'}</strong>
            )}
          />
          {isEditing && (
            <>
              <div className={classes.detailsSeparator} />
              <AceTextField
                editable={isEditing}
                control={control}
                name={`estates.${estateID}.StreetName`}
                label="Street name:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
              <AceTextField
                editable={isEditing}
                control={control}
                name={`estates.${estateID}.StreetNumber`}
                label="Street number:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
              <AceTextField
                editable={isEditing}
                control={control}
                name={`estates.${estateID}.StreetLetter`}
                label="Street letter:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
              <AceTextField
                editable={isEditing}
                control={control}
                name={`estates.${estateID}.Postcode`}
                label="Postcode:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
              <AceTextField
                editable={isEditing}
                control={control}
                name={`estates.${estateID}.Municipality`}
                label="Municipality:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
            </>
          )}
          <AceNumberField
            editable={isEditing}
            control={control}
            name={`estates.${estateID}.PrimaryLivingArea`}
            label="Primary living area:"
            formatter={(value) => (
              <strong>{value ? String(value) : '-'}</strong>
            )}
          />
          {!isEditing ? (
            <AceGenericFieldLayout
              label="Cadastre:"
              valueElement={<strong>{concatenedCadastre}</strong>}
              inputElement={null}
            />
          ) : (
            <>
              <div className={classes.detailsSeparator} />
              <AceTextField
                editable={isEditing}
                control={control}
                name={`estates.${estateID}.CadastreBNumber`}
                label="Cadastre B number:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
              <AceTextField
                editable={isEditing}
                control={control}
                name={`estates.${estateID}.CadastreFNumber`}
                label="Cadastre F number:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
              <AceTextField
                editable={isEditing}
                control={control}
                name={`estates.${estateID}.CadastreGNumber`}
                label="Cadastre G number:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
              <AceTextField
                editable={isEditing}
                control={control}
                name={`estates.${estateID}.CadastreKNumber`}
                label="Cadastre K number:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
              <AceTextField
                editable={isEditing}
                control={control}
                name={`estates.${estateID}.CadastreSNumber`}
                label="Cadastre S number:"
                formatter={(value) => (
                  <strong>{value ? String(value) : '-'}</strong>
                )}
              />
            </>
          )}
          <div className={classes.detailsSeparator} />
          <AceCheckboxField
            editable={isEditing}
            control={control}
            name={`estates.${estateID}.IsInsured`}
            label="Insured:"
            formatter={(value) => <strong>{value ? 'Yes' : 'No'}</strong>}
          />
          <AceTextField
            editable={isEditing}
            control={control}
            name={`estates.${estateID}.InsuranceCompany`}
            label="Insurance company:"
            formatter={(value) => (
              <strong>{value ? String(value) : '-'}</strong>
            )}
          />
          <div className={classes.detailsSeparator} />
          <AceNumberField
            editable={isEditing}
            control={control}
            name={`estates.${estateID}.CooperativeApartmentCommonDebt`}
            label="Cooperative apartment common debt:"
            formatter={(value) => <strong>{formatCurrency(value)}</strong>}
          />
          <AceTextField
            editable={isEditing}
            control={control}
            name={`estates.${estateID}.CooperativeApartmentOrganizationNumber`}
            label="Cooperative apartment organization number:"
            formatter={(value) => (
              <strong>{value ? String(value) : '-'}</strong>
            )}
          />
          <AceTextField
            editable={isEditing}
            control={control}
            name={`estates.${estateID}.CooperativeApartmentShareNumber`}
            label="Cooperative apartment share number:"
            formatter={(value) => (
              <strong>{value ? String(value) : '-'}</strong>
            )}
          />
        </div>
      )}
      <div className={classes.actionsContainer}>
        {isEditing && (
          <Button
            loading={
              formState.isSubmitting ||
              isDeletingEstate ||
              isRemovingApplicationCollateral
            }
            icon="cross"
            onClick={() => {
              setIsEditing({ id: estateID, isEditing: false });
              resetField(`estates.${estateID}`);
            }}
          >
            Discard
          </Button>
        )}
        {!isEditing && (
          <ConditionalTooltip
            conditions={[
              {
                isTooltipVisible: Boolean(
                  applicationData?.Status &&
                    isFinalApplicationStatus(applicationData.Status),
                ),
                tooltipContent:
                  "This application is in a final status and can't be edited",
                isChildDisabled: true,
              },
              {
                isTooltipVisible: !hasLock,
                isChildDisabled: true,
                tooltipContent: "You don't own the lock for this application",
              },
            ]}
            render={(disabled) => (
              <Button
                disabled={disabled}
                loading={
                  formState.isSubmitting ||
                  isDeletingEstate ||
                  isRemovingApplicationCollateral
                }
                icon="edit"
                intent="primary"
                onClick={() => {
                  setIsEditing({ id: estateID, isEditing: true });
                }}
              >
                Edit
              </Button>
            )}
          />
        )}
        <ConditionalTooltip
          conditions={[
            {
              isTooltipVisible: Boolean(
                applicationData?.Status &&
                  isFinalApplicationStatus(applicationData.Status),
              ),
              tooltipContent:
                "This application is in a final status and can't be deleted",
              isChildDisabled: true,
            },
            {
              isTooltipVisible: !hasLock,
              isChildDisabled: true,
              tooltipContent: "You don't own the lock for this application",
            },
          ]}
          render={(disabled) => (
            <Button
              loading={
                formState.isSubmitting ||
                isDeletingEstate ||
                isRemovingApplicationCollateral
              }
              icon="trash"
              intent="danger"
              disabled={disabled}
              onClick={() => {
                confirm({
                  message: 'Are you sure you want to delete this estate item?',
                  cancelText: 'Cancel',
                  confirmText: 'Delete',
                  confirmCallback: async () => {
                    if (!localOnlyIds.has(estateID)) {
                      if (collateralID) {
                        await removeApplicationCollateral({
                          applicationID,
                          collateralID,
                        });
                      }
                      await deleteEstate({ id: estateID });
                    }
                    setIsEditing({ id: estateID, isEditing: false });
                  },
                });
              }}
            />
          )}
        />
      </div>
    </div>
  );
};
